import { measureText } from "@remotion/layout-utils";
import type { SubSegment, WhisperJson } from "@teksta/shared-types";
import { top250FontsMap } from "@/lib/remotion/fonts/top250-fonts";
import { getHasWordTimestamps } from "./../transcription-job/whisper-json";
import { ClipsList, DEFAULT_CLIPS_CONFIG, getWordsFromSegment, WordBuffer } from "./clips";
import { getSegmentsFromSrt } from "./srt";

export { ClipsList, DEFAULT_CLIPS_CONFIG, getWordsFromSegment, WordBuffer } from "./clips";

const segmentIsInClips = ({
  segment,
  clips,
}: {
  segment: { start: number; end: number };
  clips: { startFrom: number; endAt: number }[];
}) => {
  if (clips.length === 0) {
    return true;
  }
  return clips.some((clip) => segment.end >= clip.startFrom && segment.start <= clip.endAt);
};

const adjustTimesByClips = <T extends { start: number; end: number }>({
  segment,
  clips,
}: {
  segment: T;
  clips: { startFrom: number; endAt: number }[];
}): T => {
  const clip = clips.find((clip) => segment.end >= clip.startFrom && segment.start <= clip.endAt);
  if (clip) {
    const adjustedStart = segment.start - clip.startFrom;
    const adjustedEnd = segment.end - clip.startFrom;
    return {
      ...segment,
      start: Math.max(adjustedStart, 0),
      end: Math.min(adjustedEnd, clip.endAt - clip.startFrom),
    };
  }
  return segment;
};

export const getSubSegmentsFromWhisperJson = ({
  whisperJson,
  stopCharacters = DEFAULT_CLIPS_CONFIG.stopCharacters,
  maxDurationS = DEFAULT_CLIPS_CONFIG.maxDurationS,
  splitAfterGapS = DEFAULT_CLIPS_CONFIG.splitAfterGapS,
  maxChars = DEFAULT_CLIPS_CONFIG.maxChars,
  originalSegments,
  clips,
  textPreviewMs,
}: {
  whisperJson: WhisperJson;
  clips: { startFrom: number; endAt: number }[];
} & Partial<SegmentConfig>) => {
  const clipsList = new ClipsList();

  const hasWordTimestamps = getHasWordTimestamps(whisperJson);
  const shouldSpaceWords = !["th"].includes(whisperJson.language);

  // 1) Figure out which segments we want to use
  const segmentsToUse = getSegmentsToUse({
    whisperJson,
    clips,
    hasWordTimestamps,
    shouldSpaceWords,
  });

  // 2) If user wants original segments (with karaoke-style word timings), build them
  if (originalSegments) {
    buildClipsListForOriginalSegments({
      segments: segmentsToUse,
      textPreviewMs: textPreviewMs ?? 0,
      clipsList,
    });
    return clipsList;
  }

  // 3) Otherwise, do a word-buffer approach (split or group words based on duration, gap, etc.)
  buildDynamicClipsList({
    segments: segmentsToUse,
    clips,
    stopCharacters,
    maxDurationS,
    splitAfterGapS,
    maxChars,
    clipsList,
  });

  return clipsList;
};

/* -----------------------------------------------
   Helper function #1
   Filter, clip, and/or reconstruct segments if
   we don't have word timestamps, or if we do
   but want them in a certain format.
------------------------------------------------ */
function getSegmentsToUse({
  whisperJson,
  clips,
  hasWordTimestamps,
  shouldSpaceWords,
}: {
  whisperJson: WhisperJson;
  clips: { startFrom: number; endAt: number }[];
  hasWordTimestamps: boolean;
  shouldSpaceWords: boolean;
}): SubSegment[] {
  // 1) Filter only segments in selected clips range
  const filteredSegments = whisperJson.segments
    .filter((segment) => segmentIsInClips({ segment, clips }))
    .map((segment) => adjustTimesByClips({ segment, clips }));

  // 2) If we have word timestamps and we want them, keep them
  //    Otherwise, generate them from text (no real timestamps).
  if (hasWordTimestamps && shouldSpaceWords) {
    return filteredSegments;
  }

  // 3) Build a simple list of words for each segment
  return filteredSegments.map((segment) => {
    const words = getWordsFromSegment({
      text: segment.text,
      start: segment.start,
      end: segment.end,
    }).map((w) => ({
      word: w.text,
      start: w.startTime,
      end: w.endTime,
    }));

    return {
      words,
      start: segment.start,
      end: segment.end,
      text: segment.text,
    };
  });
}

/* -----------------------------------------------
   Helper function #2
   When originalSegments == true, we do the partial
   extension of segment boundaries, fix word-level
   gaps, zero durations, etc.
------------------------------------------------ */
function buildClipsListForOriginalSegments({
  segments,
  textPreviewMs,
  clipsList,
}: {
  segments: SubSegment[];
  textPreviewMs: number;
  clipsList: ClipsList;
}) {
  // 1) Adjust overall segment boundaries (20%/80% preview split)
  const textPreviewSec = textPreviewMs / 1000;
  const adjustedTimes = adjustSegmentBoundaries(segments, textPreviewSec);

  // 2) Create final SubClips with word-level cleanup
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const { start, end } = adjustedTimes[i];

    // Build word array with original times
    const words = (seg.words ?? []).map((w) => ({
      text: w.word.trim(),
      startTime: w.start,
      endTime: w.end,
    }));

    // Snap consecutive words back-to-back if there's a gap
    removeIntraSegmentWordGaps(words);

    // Fix any zero-duration words by borrowing up to 10% from neighbors
    fixZeroDurationWords(words);

    // Add final subclip to the list
    clipsList.add(seg.text, start, end, "original", words);
  }
}

/* -----------------------------------------------
   Helper function #2a
   Adjust overall segment boundaries with the
   partial extension logic, ensuring no overlaps.
------------------------------------------------ */
function adjustSegmentBoundaries(
  segments: SubSegment[],
  textPreviewSec: number
): { start: number; end: number }[] {
  const adjustedTimes = segments.map((seg) => ({
    start: seg.start,
    end: seg.end,
  }));

  for (let i = 0; i < segments.length - 1; i++) {
    const cur = adjustedTimes[i];
    const nxt = adjustedTimes[i + 1];
    const gap = nxt.start - cur.end;

    // If there's a positive gap, do partial extension
    if (gap > 0) {
      const usablePreview = Math.min(gap, textPreviewSec);

      // 20% extends current segment’s end
      const extendPrev = 0.2 * usablePreview;
      // 80% makes next segment start earlier
      const bringNextSooner = 0.8 * usablePreview;

      // Extend the current segment’s end, if no overlap
      let newEnd = cur.end + extendPrev;
      if (newEnd > nxt.start) {
        newEnd = cur.end; // revert if overlap
      }
      cur.end = newEnd;

      // Bring the next segment’s start earlier, if no overlap
      let newStart = nxt.start - bringNextSooner;
      if (newStart < cur.end) {
        newStart = nxt.start; // revert if overlap
      }
      // never push it later than original
      if (newStart > segments[i + 1].start) {
        newStart = segments[i + 1].start;
      }
      nxt.start = newStart;
    }
  }

  return adjustedTimes;
}

/* -----------------------------------------------
   Helper function #2b
   Remove word gaps inside a single segment
   by snapping each word's end to the next's start.
------------------------------------------------ */
function removeIntraSegmentWordGaps(words: { text: string; startTime: number; endTime: number }[]) {
  for (let w = 0; w < words.length - 1; w++) {
    const current = words[w];
    const next = words[w + 1];
    if (current.endTime < next.startTime) {
      current.endTime = next.startTime;
    }
  }
}

/* -----------------------------------------------
   Helper function #2c
   Fix zero-duration words by "borrowing" up to
   10% of neighbors' durations (5% + 5%) if possible.
------------------------------------------------ */
function fixZeroDurationWords(words: { text: string; startTime: number; endTime: number }[]) {
  for (let w = 0; w < words.length; w++) {
    const current = words[w];
    const duration = current.endTime - current.startTime;

    if (duration > 0.000001) {
      continue; // skip normal words
    }

    // Attempt to borrow from neighbors
    const left = w > 0 ? words[w - 1] : null;
    const right = w < words.length - 1 ? words[w + 1] : null;

    const borrowFraction = 0.1; // up to 10% total
    let borrowed = 0;

    // Borrow from left neighbor?
    if (left) {
      const leftDur = left.endTime - left.startTime;
      if (leftDur > 0.000001) {
        const fraction = right ? 0.05 : borrowFraction;
        const canBorrow = fraction * leftDur;
        const newLeftDur = leftDur - canBorrow;
        if (newLeftDur >= 0) {
          left.endTime -= canBorrow;
          borrowed += canBorrow;
        }
      }
    }

    // Borrow from right neighbor?
    if (right) {
      const rightDur = right.endTime - right.startTime;
      if (rightDur > 0.000001) {
        const fraction = left ? 0.05 : borrowFraction;
        const canBorrow = fraction * rightDur;
        const newRightDur = rightDur - canBorrow;
        if (newRightDur >= 0) {
          right.startTime += canBorrow;
          borrowed += canBorrow;
        }
      }
    }

    // Now assign the borrowed time to the current word
    if (borrowed > 0.000001) {
      let newEnd = current.startTime + borrowed;
      if (right && newEnd > right.startTime) {
        newEnd = right.startTime; // clamp to avoid overlap
      }
      current.endTime = newEnd;
    }
    // Otherwise, it remains zero-length
  }
}

/* -----------------------------------------------
   Helper function #3
   If originalSegments == false, we proceed to
   collect words in a buffer, splitting or grouping
   based on maxDurationS, splitAfterGapS, etc.
------------------------------------------------ */
function buildDynamicClipsList({
  segments,
  clips,
  stopCharacters,
  maxDurationS,
  splitAfterGapS,
  maxChars,
  clipsList,
}: {
  segments: SubSegment[];
  clips: { startFrom: number; endAt: number }[];
  stopCharacters: string[];
  maxDurationS: number;
  splitAfterGapS: number;
  maxChars: number;
  clipsList: ClipsList;
}) {
  const wordBuffer = new WordBuffer();

  // Flatten all words from all segments and filter
  const allWords = segments
    .flatMap((segment) => segment.words ?? [])
    .filter((word) => word.word.trim().length > 0)
    .filter((segment) => segmentIsInClips({ segment, clips }))
    .map((segment) => adjustTimesByClips({ segment, clips }));

  // Iterate each word, building subclips from buffer
  allWords.forEach((word, i) => {
    const nextWord = allWords[i + 1];

    // flush if adding this word would exceed maxDuration
    if (maxDurationS && wordBuffer.duration + (word.end - word.start) > maxDurationS) {
      const result = wordBuffer.flush();
      clipsList.add(result.text, result.start, result.end, "maxDuration", result.words);
    }

    // flush if there's a gap
    if (splitAfterGapS && nextWord && nextWord.start - word.end > splitAfterGapS) {
      const result = wordBuffer.flush();
      clipsList.add(result.text, result.start, result.end, "splitAfterGap", result.words);
    }

    // flush if we exceed max chars
    if (maxChars && wordBuffer.text.length >= maxChars) {
      const result = wordBuffer.flush();
      clipsList.add(result.text, result.start, result.end, "maxChars", result.words);
    }

    // add this word
    wordBuffer.add(word);

    // flush if it ends with a stop character
    if (stopCharacters.includes(word.word.trim().at(-1)!)) {
      const result = wordBuffer.flush();
      clipsList.add(result.text, result.start, result.end, "stopCharacter", result.words);
    }
  });

  // flush leftover
  const result = wordBuffer.flush();
  if (result.text.trim()) {
    clipsList.add(result.text, result.start, result.end, "endOfWords", result.words);
  }
}

export const splitSegmentIntoWordLines = ({
  segment,
  width,
  styleConfig,
}: {
  segment: SubClip;
  width: number;
  styleConfig: StyleConfig;
}): SegmentWord[][] => {
  const fontObject =
    top250FontsMap[styleConfig.layer.font.toLowerCase() as keyof typeof top250FontsMap];

  // Step 1: Preprocess words to split at '\n' and group into initial lines
  const initialLines: SegmentWord[][] = [];
  let currentLineWords: SegmentWord[] = [];

  segment.words.forEach((word) => {
    if (word.text.includes("\n")) {
      // Split the word at '\n'
      const parts = word.text.split("\n");
      const timings = {
        startTime: word.startTime,
        endTime: word.endTime,
      };

      // Add the part before '\n' to the current line
      if (parts[0]) {
        currentLineWords.push({
          ...word,
          text: parts[0],
          ...timings,
        });
      }

      // Add the current line to initialLines
      initialLines.push(currentLineWords);
      currentLineWords = []; // Start a new line

      // If there's text after '\n', start the new line with it
      if (parts[1]) {
        currentLineWords.push({
          ...word,
          text: parts[1],
          ...timings,
        });
      }
    } else {
      // No '\n' in this word, add it to the current line
      currentLineWords.push(word);
    }
  });

  // Add any remaining words as the last line
  if (currentLineWords.length > 0) {
    initialLines.push(currentLineWords);
  }

  // Step 2: Process each line to fit within width constraints
  const result: SegmentWord[][] = [];

  initialLines.forEach((lineWords) => {
    // Recursively split line if necessary
    const splitLines = splitLineIntoWordLines({
      words: lineWords,
      width,
      styleConfig,
      result: [],
    });
    result.push(...splitLines);
  });

  return result;
};

// Helper function to split a line of words into multiple lines based on width
const splitLineIntoWordLines = ({
  words,
  width,
  styleConfig,
  result,
}: {
  words: SegmentWord[];
  width: number;
  styleConfig: StyleConfig;
  result: SegmentWord[][];
}): SegmentWord[][] => {
  const fontObject =
    top250FontsMap[styleConfig.layer.font.toLowerCase() as keyof typeof top250FontsMap];
  const fontFamily = fontObject?.fontFamily || "sans-serif";
  const fontSize = styleConfig.layer.fontSize;
  const fontWeight = styleConfig.layer.fontWeight;

  const segmentText = words.map((word) => word.text).join(" ");

  const { width: textWidth } = measureText({
    fontFamily,
    fontSize,
    fontWeight,
    text: segmentText,
  });

  const totalXSpace =
    (styleConfig.layer.paddingX + styleConfig.layer.marginX) * styleConfig.layer.fontSize * 2;

  if (textWidth + totalXSpace + 4 > width) {
    // Not getting this right, adding 4px buffer
    if (words.length === 1) {
      // Single word exceeds max width, split the word
      const halfWordIndex = Math.floor(words[0].text.length / 2);
      const firstHalf = words[0].text.slice(0, halfWordIndex) + "-";
      const secondHalf = words[0].text.slice(halfWordIndex);
      return [
        ...splitLineIntoWordLines({
          words: [
            {
              ...words[0],
              text: firstHalf,
            },
          ],
          width,
          styleConfig,
          result,
        }),
        ...splitLineIntoWordLines({
          words: [
            {
              ...words[0],
              text: secondHalf,
            },
          ],
          width,
          styleConfig,
          result,
        }),
      ];
    } else {
      // Split the line into multiple lines
      const halfWordsIndex = Math.floor(words.length / 2);
      const firstHalf = words.slice(0, halfWordsIndex);
      const secondHalf = words.slice(halfWordsIndex);
      return [
        ...splitLineIntoWordLines({
          words: firstHalf,
          width,
          styleConfig,
          result,
        }),
        ...splitLineIntoWordLines({
          words: secondHalf,
          width,
          styleConfig,
          result,
        }),
      ];
    }
  } else {
    return [words];
  }
};

export const splitSegment = ({
  segment,
  width,
  result,
  styleConfig,
  recursionLevel = 0,
}: {
  segment: SubClip;
  width: number;
  result: string[];
  styleConfig: StyleConfig;
  recursionLevel?: number;
}): string[] => {
  if (recursionLevel > 5) {
    console.warn("Recursion level too deep");
    return [...result, segment.text];
  }

  const fontObject = top250FontsMap[styleConfig.layer.font as keyof typeof top250FontsMap];

  const { width: textWidth } = measureText({
    fontFamily: fontObject?.fontFamily,
    fontSize: styleConfig.layer.fontSize,
    fontWeight: styleConfig.layer.fontWeight,
    text: segment.text,
  });

  const totalXSpace =
    (styleConfig.layer.paddingX + styleConfig.layer.marginX) * styleConfig.layer.fontSize * 2;

  if (textWidth + totalXSpace > width) {
    const segmentWords = segment.text.split(" ");
    if (segmentWords.length === 1) {
      const firstHalf = segment.text.slice(0, Math.floor(segment.text.length / 2)) + "-";
      const secondHalf = segment.text.slice(Math.floor(segment.text.length / 2));
      return [
        ...result,
        ...splitSegment({
          segment: {
            ...segment,
            text: firstHalf,
          },
          width,
          result: [],
          styleConfig,
          recursionLevel: recursionLevel + 1,
        }),
        ...splitSegment({
          segment: {
            ...segment,
            text: secondHalf,
          },
          width,
          result: [],
          styleConfig,
          recursionLevel: recursionLevel + 1,
        }),
      ];
    }

    const [firstHalf, secondHalf] = splitTextAtHalf({
      text: segment.text,
    });

    return [
      ...result,
      ...splitSegment({
        segment: {
          ...segment,
          text: firstHalf,
        },
        width,
        result: [],
        styleConfig,
        recursionLevel: recursionLevel + 1,
      }),
      ...splitSegment({
        segment: {
          ...segment,
          text: secondHalf,
        },
        width,
        result: [],
        styleConfig,
        recursionLevel: recursionLevel + 1,
      }),
    ];
  } else {
    return [...result, segment.text];
  }
};

export const splitTextAtHalf = ({ text }: { text: string }) => {
  const halfLengthChars = Math.floor(text.length / 2);
  const firstSpaceAfterHalf = text.indexOf(" ", halfLengthChars);
  const firstSpaceBeforeHalf = text.lastIndexOf(" ", halfLengthChars);
  const distanceAfter = firstSpaceAfterHalf - halfLengthChars;
  const distanceBefore = halfLengthChars - firstSpaceBeforeHalf;
  let firstHalf = "";
  let secondHalf = "";
  if (firstSpaceAfterHalf !== -1 && distanceAfter <= distanceBefore) {
    firstHalf = text.slice(0, firstSpaceAfterHalf);
    secondHalf = text.slice(firstSpaceAfterHalf + 1);
  } else {
    firstHalf = text.slice(0, firstSpaceBeforeHalf);
    secondHalf = text.slice(firstSpaceBeforeHalf + 1);
  }
  return [firstHalf, secondHalf];
};

export const splitWordsAtHalf = ({ words }: { words: SegmentWord[] }) => {
  const halfLength = Math.floor(words.length / 2);
  const firstHalf = words.slice(0, halfLength);
  const secondHalf = words.slice(halfLength);
  return [firstHalf, secondHalf];
};

export const getSubSegmentsFromSrt = ({
  srt,
  clips,
}: {
  srt: string;
  clips: { startFrom: number; endAt: number }[];
}) => {
  const clipsList = getSegmentsFromSrt({ srt });

  if (clips.length > 0) {
    clipsList.clips = clipsList.clips
      .filter((segment) => {
        return clips.some((clip) => {
          return segment.startTime >= clip.startFrom && segment.endTime <= clip.endAt;
        });
      })
      .map((segment) => {
        const clip = clips.find(
          (clip) => segment.startTime >= clip.startFrom && segment.endTime <= clip.endAt
        );
        if (clip) {
          return {
            ...segment,
            startTime: segment.startTime - clip.startFrom,
            endTime: segment.endTime - clip.startFrom,
          };
        }
        return segment;
      });
  }
  return clipsList;
};
